import type { EntityDto } from "api/types";

import { GAP_X, LEVEL_HEIGHT, NODE_WIDTH } from "./treeGraph.constants";

type Point = { x: number; y: number };

/** Resting horizontal distance between two related cards. */
const IDEAL_DISTANCE = 300;
/** Simulation steps; enough for graphs of this size to settle. */
const ITERATIONS = 250;
/** Pull toward the center line so disconnected clusters stay on one canvas. */
const GRAVITY = 0.03;
/** Cards on one row closer than this (center to center) get pushed apart after the simulation. */
const MIN_SEPARATION = NODE_WIDTH + GAP_X;
const SEPARATION_PASSES = 80;
/**
 * Past this gap two cards are simply unrelated neighbours and should stop shoving each other.
 * Without a cutoff the 1/gap falloff still pushes at long range, and a card with few or no
 * relations has nothing but gravity holding it: it drifts until 0.03x balances the crowd, which
 * lands it thousands of pixels out. fitView then has to frame that span and clamps to minZoom,
 * rendering every card at a fraction of its size.
 */
const REPULSION_CUTOFF = IDEAL_DISTANCE * 1.5;

/**
 * Force-directed layout constrained to generation rows. A card's row comes from the same generation
 * levels the hierarchical view uses, so a parent or predecessor always sits one row above their child;
 * the simulation only decides where along its row each card sits. Related cards attract horizontally
 * (pulling children under their parents), cards sharing a row repel, and a light gravity keeps
 * disconnected clusters together. Deterministic — rows seed in entity order and run a fixed number of
 * cooling steps — so the same tree always settles into the same shape.
 */
export const computeForcePositions = (
	entities: EntityDto[],
	adjacency: Map<string, string[]>,
	levelById: Map<string, number>
): Map<string, Point> => {
	const positions = new Map<string, Point>();
	if (entities.length === 0) {
		return positions;
	}

	const membersByLevel = new Map<number, string[]>();
	entities.forEach((entity: EntityDto) => {
		const level = levelById.get(entity.id) ?? 0;
		if (!membersByLevel.has(level)) {
			membersByLevel.set(level, []);
		}
		membersByLevel.get(level)!.push(entity.id);
	});

	// Deterministic seed: every generation becomes a row, centered and spread in entity order.
	// Generations nobody occupies are squeezed out so the canvas has no empty bands.
	const rowByEntity = new Map<string, number>();
	const levels = Array.from(membersByLevel.keys()).sort((a: number, b: number) => a - b);
	levels.forEach((level: number, rowIndex: number) => {
		const members = membersByLevel.get(level)!;
		const startX = -((members.length - 1) * IDEAL_DISTANCE) / 2;
		members.forEach((id: string, index: number) => {
			rowByEntity.set(id, rowIndex);
			positions.set(id, { x: startX + index * IDEAL_DISTANCE, y: rowIndex * LEVEL_HEIGHT });
		});
	});

	// Unique springs from the (mirrored, possibly duplicated) adjacency.
	const springKeys = new Set<string>();
	const springs: [string, string][] = [];
	adjacency.forEach((neighbors: string[], id: string) => {
		neighbors.forEach((otherId: string) => {
			if (!positions.has(otherId) || !positions.has(id) || id === otherId) {
				return;
			}

			const key = id < otherId ? `${id}|${otherId}` : `${otherId}|${id}`;
			if (!springKeys.has(key)) {
				springKeys.add(key);
				springs.push(id < otherId ? [id, otherId] : [otherId, id]);
			}
		});
	});

	const ids = entities.map((entity: EntityDto) => entity.id);
	const sharesRow = (aId: string, bId: string): boolean => rowByEntity.get(aId) === rowByEntity.get(bId);
	const initialTemperature = IDEAL_DISTANCE * 2;

	for (let iteration = 0; iteration < ITERATIONS; iteration += 1) {
		// Linear cooling: large moves early, tiny settling nudges at the end.
		const temperature = initialTemperature * (1 - iteration / ITERATIONS);
		const displacement = new Map<string, number>();
		ids.forEach((id: string) => displacement.set(id, 0));
		const push = (id: string, dx: number): void => {
			displacement.set(id, displacement.get(id)! + dx);
		};

		// Repulsion between cards sharing a row; other rows never shove each other sideways.
		for (let i = 0; i < ids.length; i += 1) {
			for (let j = i + 1; j < ids.length; j += 1) {
				if (!sharesRow(ids[i], ids[j])) {
					continue;
				}

				const dx = positions.get(ids[i])!.x - positions.get(ids[j])!.x;
				const gap = Math.max(Math.abs(dx), 1);
				if (gap > REPULSION_CUTOFF) {
					continue;
				}

				const direction = dx < 0 ? -1 : 1;
				const force = (IDEAL_DISTANCE * IDEAL_DISTANCE) / gap;
				push(ids[i], direction * force);
				push(ids[j], -direction * force);
			}
		}

		// Attraction along every relation, which lines children up under their parents.
		springs.forEach(([aId, bId]: [string, string]) => {
			const dx = positions.get(aId)!.x - positions.get(bId)!.x;
			const gap = Math.max(Math.abs(dx), 1);
			const direction = dx < 0 ? -1 : 1;
			const force = (gap * gap) / IDEAL_DISTANCE;
			push(aId, -direction * force);
			push(bId, direction * force);
		});

		// Apply displacements, capped by the cooling temperature, with gravity toward the center line.
		ids.forEach((id: string) => {
			const position = positions.get(id)!;
			const move = displacement.get(id)! - position.x * GRAVITY;
			const limited = Math.max(Math.min(move, temperature), -temperature);
			positions.set(id, { x: position.x + limited, y: position.y });
		});
	}

	// Separate any same-row cards the simulation left overlapping.
	for (let pass = 0; pass < SEPARATION_PASSES; pass += 1) {
		let moved = false;
		for (let i = 0; i < ids.length; i += 1) {
			for (let j = i + 1; j < ids.length; j += 1) {
				if (!sharesRow(ids[i], ids[j])) {
					continue;
				}

				const a = positions.get(ids[i])!;
				const b = positions.get(ids[j])!;
				const dx = a.x - b.x;
				const gap = Math.abs(dx);
				if (gap < MIN_SEPARATION) {
					const shift = (MIN_SEPARATION - gap) / 2;
					const direction = dx < 0 ? -1 : 1;
					positions.set(ids[i], { x: a.x + direction * shift, y: a.y });
					positions.set(ids[j], { x: b.x - direction * shift, y: b.y });
					moved = true;
				}
			}
		}
		if (!moved) {
			break;
		}
	}

	// xyflow positions are top-left corners: the row already is one, so only the simulated center shifts.
	ids.forEach((id: string) => {
		const position = positions.get(id)!;
		positions.set(id, { x: position.x - NODE_WIDTH / 2, y: position.y });
	});

	return positions;
};
